<template>
  <div class="filter-wrapper">
    <div
      class="filter"
    >
      <div class="filter__container__header">
        <div class="filter__container__header-title-section">
          <div class="rich-text">
            <h5>{{ title }}</h5>
          </div>
          <div
            class="filter__container__header-title-section--icon"
            @click="onClose()"
          >
            <Icon
              name="times"
              size="24"
            />
          </div>
        </div>
        <div class="filter__container__header-save-section">
          <div class="rich-text">
            <span class="small">{{ currentFilter || "Custom Filter" }}</span>
            <span
              v-if="isFilterModified"
              class="small edited"
            > - Edited</span>
          </div>
          <Button
            v-if="isFilterModified"
            label="Save as"
            small
            @onClick="onSave(!!currentFilter)"
          />
        </div>
      </div>
      <div class="filter-body">
        <div
          v-for="(fieldMapping, key) in configuration.fieldMappings"
          :key="key"
        >
          <checkbox-filter
            v-if="fieldMapping.type === 'checkbox' && fieldMapping.isVisible"
            :title="key"
            :expanded="fieldMapping.expanded"
            :field-data="fieldMapping"
            :options="facets[fieldMapping.fieldName]"
            :values="
              filterData[key] && filterData[key][0]
                ? filterData[key][0].values
                : []
            "
            @onCardToggle="onCardToggle"
          />
          <date-filter
            v-if="fieldMapping.type === 'date' && fieldMapping.isVisible"
            :title="key"
            :expanded="fieldMapping.expanded"
            :field-data="fieldMapping"
            :options="facets[fieldMapping.fieldName]"
            :from-date="filterData[key] ? filterData[key].from : ''"
            :to-date="filterData[key] ? filterData[key].to : ''"
            @onCardToggle="onCardToggle"
          />
        </div>
      </div>
      <div class="filter-footer">
        <div class="filter-footer__tertiary-block">
          <Button
            label="Configure filters"
            appearance="tertiary"
            icon-position="left"
            icon="sliders-horizontal"
            transparent
            @onClick="toggleConfigFilterModal"
          />
          <Button
            label="Saved Filters"
            appearance="tertiary"
            icon-position="left"
            icon="floppy-disk"
            transparent
            @onClick="toggleSavedFilterModal"
          />
        </div>
        <div
          v-if="showApplyButton"
          class="filter-footer__save-block"
        >
          <Button
            label="Apply Filter"
            appearance="primary"
            full-width
          />
        </div>
        <div
          class="filter-footer__clear-block"
          @click="onClear()"
        >
          <span>Clear Filter</span>
        </div>
      </div>
    </div>
    <configure-filter-modal
      v-show="configFilterModalIsOpen"
      :is-open="configFilterModalIsOpen"
      :configuration="configuration.fieldMappings"
      :default-columns="defaultColumns"
      :cache-name="cacheName"
      @onModalClose="toggleConfigFilterModal"
      @onSaveColumnConfig="onSaveColumnConfig"
    ></configure-filter-modal>
    <saved-filter-modal
      v-show="savedFilterModalIsOpen"
      :is-open="savedFilterModalIsOpen"
      :cache-name="cacheName"
      :saved-filters="savedFilters"
      @onModalClose="toggleSavedFilterModal"
    ></saved-filter-modal>
    <save-new-filter-modal
      v-show="saveNewFilterModalIsOpen"
      :is-open="saveNewFilterModalIsOpen"
      :cache-name="cacheName"
      :is-update="isExistingFilter"
      :saved-filters="savedFilters"
      @onModalClose="toggleSaveNewFilterModal"
    ></save-new-filter-modal>
  </div>
</template>

<script>
// Common components
import { Button, Icon } from "../../components";
// Filter dependent component
import CheckboxFilter from "./CheckboxFilter";
import DateFilter from "./DateFilter";
import ConfigureFilterModal from "./ConfigureFilterModal";
import SavedFilterModal from "./SavedFilterModal";
import SaveNewFilterModal from "./SaveNewFilterModal";


export default {
  name: "FilterBar",
  components: {
    Icon,
    Button,
    CheckboxFilter,
    DateFilter,
    ConfigureFilterModal,
    SavedFilterModal,
    SaveNewFilterModal,
  },
  props: {
    /**
     * @description
     * Title on top the FilterBar
    */
    title: { type: String, default: 'Filter' },
    /**
     * @description
     * Configuraton for rendering list of filters and it's default value,
     * Configuration Object has fieldMappings and defaultValues
    */
    configuration: { type: Object, required: true },
    /**
     * @description
     * Default columns to be displayed on the FilterBar,
    */
    defaultColumns: { type: Array, required: true },
    /**
     * @description
     * Facets objects holds list of filter column values and its counts for each column to be displayed on the FilterBar,
    */
    facets: { type: Object, required: true },
    /**
     * @description
     * filterData object contains current filter values,
    */
    filterData: { type: Object, required: true },
    /**
     * @description
     * When Apply filter button is enabled, the updated filter changes will be saved only on click of save button instead of updating on every filter value onChange.
    */
    showApplyButton: { type: Boolean, default: false },
    /**
     * @description
     * It is used storing the filter configurations on localstorage with given cacheName prefix
    */
    cacheName: { type: String, default: '' },
    /**
     * @description
     * List of saved filters
    */
    savedFilters: { type: Array, default: () => { } },
    /**
     * @description
     * Name of the currently applied filter
    */
    currentFilter: { type: String, default: '' },
    /**
     * @description
     * Name of the currently applied filter
    */
  },
  data () {
    return {
      configFilterModalIsOpen: false,
      savedFilterModalIsOpen: false,
      saveNewFilterModalIsOpen: false,
      isFilterModified: false,
      isExistingFilter: false
    }
  },
  methods: {
    /**
     * @description 
     * OnClose callback is triggered when user clicks on FilerBar close button
     */
    onClose () {
      this.$emit("onClose");
    },
    /**
     * @description
     * OnClose method is triggered when user toggles a particular filter card,
     * Emits onCardToggle callback with cardName as a parameter
     * @param {string} cardName - Name of the toggled card
    */
    onCardToggle (cardName) {
      this.$emit("onCardToggle", cardName);
    },
    /**
     * @description
     * toggleConfigFilterModal method is triggered when user clicks on a Configure filters button,
    */
    toggleConfigFilterModal () {
      this.configFilterModalIsOpen = !this.configFilterModalIsOpen;
    },
    /**
     * @description
     * toggleSavedFilterModal method is triggered when user clicks on a Saved Filters button,
    */
    toggleSavedFilterModal () {
      this.savedFilterModalIsOpen = !this.savedFilterModalIsOpen;
    },
    /**
     * @description
     * toggleSaveNewFilterModal method is triggered when user closes the Save new Filter Modal, isFilterModified is set to false 
    */
    toggleSaveNewFilterModal () {
      this.saveNewFilterModalIsOpen = !this.saveNewFilterModalIsOpen;
      this.isFilterModified = false;
    },
    /**
     * @description
     * onSaveColumnConfig method is triggered when user Saves the column configuration on configure filter modal,
     * Emits onSaveColumnConfig callback with column configuraiton object as param
     * @param {object} configuration - Column configuration
    */
    onSaveColumnConfig (configuration) {
      this.$emit("onSaveColumnConfig", configuration);
    },
    /**
     * @description
     * onClear method is triggered on click of Clear Filter button,
     * Emits onFilterClear callback
    */
    onClear () {
      this.$emit("onFilterClear");
    },
    /**
     * @description
     * onSave method is triggered on click of Clear Filter button,
     * Emits onFilterClear method
     * @param {boolean} isExistingFilter 
    */
    onSave (isExistingFilter) {
      this.isExistingFilter = isExistingFilter;
      this.saveNewFilterModalIsOpen = true;
    },
    /**
     * @description
     * setFilterModified - isFilterModified flag is set,
     * @param {boolean} setFilterModified 
    */
    setFilterModified (isEdited) {
      this.isFilterModified = isEdited;
    },
  },
};
</script>

<style lang="scss" scoped>
.filter-wrapper {
  position: relative;
  width: 440px;
  & .filter {
    position: relative;
    top: 0;
    z-index: 999;
    background: var(--Gray-100);
    overflow-y: hidden;
    height: 100vh;
    display: flex;
    flex-direction: column;
    padding: 24px;
    width: inherit;
    & .filter__container__header {
      & .rich-text h5 {
        background: var(--Gray-100);
      }
      & .filter__container__header-title-section {
        justify-content: space-between;
        display: flex;
        & .filter__container__header-title-section--icon {
          cursor: pointer;
        }
      }
      & .filter__container__header-save-section {
        display: flex;
        margin: 8px 0;
        & .rich-text {
          & span {
            color: var(--Gray-900-Black);
            &.edited {
              color: var(--Gray-600);
              margin-left: 4px;
            }
          }
          & a {
            font-size: 12px;
            margin-left: 16px;
            font-weight: normal;
          }
        }
        & a.button {
          margin-left: 16px;
        }
      }
    }
    & .filter-body {
      display: flex;
      flex: 1;
      flex-direction: column;
      overflow-y: scroll;
    }
    & .filter-footer {
      margin-top: 16px;
      & .filter-footer__tertiary-block {
        display: flex;
        justify-content: space-between;
      }
      & .filter-footer__save-block {
        display: flex;
        margin: 16px 0;
      }
      & .filter-footer__clear-block {
        text-align: center;
        & span {
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }
  }
}
</style>
